import type { FC, Ref } from "react";

import {
  PASSWORD_MIN_LENGTH,
  hasLowercase,
  hasNumber,
  hasUppercase,
  isMinLength,
} from "../../helpers/password-errors";

import { t } from "i18n/i18n";
import { Tooltip, type TooltipProps, type TooltipRefProps } from "mds/components/Tooltip";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { CircleCheckFilledIcon, CircleCheckIcon } from "mds/icons";

type PasswordRequirementTooltipProps = TooltipProps & {
  ref?: Ref<TooltipRefProps>;
  isOpen: boolean;
  password: string;
};

export const PasswordRequirementTooltip: FC<PasswordRequirementTooltipProps> = ({
  ref,
  isOpen,
  password,
  ...props
}) => {
  const isSmallScreen = !useIsMdOrLarger();
  const requirementMetIcon = (isReqMet: (password: string) => boolean) =>
    isReqMet(password) ? (
      <CircleCheckFilledIcon
        aria-label={t("password_requirement.requirement_met")}
        className="text-green"
      />
    ) : (
      <CircleCheckIcon aria-label={t("password_requirement.requirement_unmet")} />
    );

  const tooltipText = (
    <>
      <div className="mb-1 text-sm font-medium">{t("password_requirement.title")}</div>
      <ul className="text-sm font-normal">
        <li className="flex flex-row-reverse items-center gap-1" key="one_number">
          <div className="flex-grow">{t("password_requirement.one_number")}</div>
          {requirementMetIcon(hasNumber)}
        </li>
        <li className="flex flex-row-reverse items-center gap-1" key="one_uppercase">
          <div className="flex-grow">{t("password_requirement.one_uppercase")}</div>
          {requirementMetIcon(hasUppercase)}
        </li>
        <li className="flex flex-row-reverse items-center gap-1" key="one_lowercase">
          <div className="flex-grow">{t("password_requirement.one_lowercase")}</div>
          {requirementMetIcon(hasLowercase)}
        </li>
        <li className="flex flex-row-reverse items-center gap-1" key="min_length">
          <div className="flex-grow">
            {t("password_requirement.min_length", { count: PASSWORD_MIN_LENGTH })}
          </div>
          {requirementMetIcon(isMinLength)}
        </li>
      </ul>
    </>
  );

  return (
    <Tooltip
      isOpen={isOpen}
      wrapperClassName="inline-block"
      {...props}
      content={tooltipText}
      place={isSmallScreen ? "top" : "right"}
      ref={ref}
    />
  );
};

PasswordRequirementTooltip.displayName = "PasswordRequirementTooltip";
